import { useCallback, useEffect, useRef } from 'react'
import {
  stripRedundantSectionHeadingFromBody,
  type ComplianceDocumentSectionRecord,
} from '@artem-lex/compliance-documents'
import type { Editor } from '@tiptap/core'
import { cn } from '@/shared/lib/utils'
import { DocumentEditor } from '@/components/document-editor'
import { getSectionHeadingLabel } from '@/features/compliance-studio/shared/compliance-document/complianceDocumentEditorUtils'

export type RecyclabilitySectionEditorRegistration = {
  sectionId: string
  editorRef: React.MutableRefObject<Editor | null>
}

type RecyclabilityAssessmentDocumentSectionBlockProps = {
  section: ComplianceDocumentSectionRecord
  sectionTitle: string
  contentSyncKey: number
  onRegister: (registration: RecyclabilitySectionEditorRegistration) => void
  onUnregister: (sectionId: string) => void
  onSectionFocus: (sectionId: string) => void
  onSectionEditorReady: (sectionId: string) => void
}

export function RecyclabilityAssessmentDocumentSectionBlock({
  section,
  sectionTitle,
  contentSyncKey,
  onRegister,
  onUnregister,
  onSectionFocus,
  onSectionEditorReady,
}: RecyclabilityAssessmentDocumentSectionBlockProps) {
  const editorRef = useRef<Editor | null>(null)

  useEffect(() => {
    onRegister({ sectionId: section.sectionId, editorRef })
    return () => {
      onUnregister(section.sectionId)
    }
  }, [section.sectionId, onRegister, onUnregister])

  const handleEditorReady = useCallback(
    (_editor: Editor) => {
      onSectionEditorReady(section.sectionId)
    },
    [onSectionEditorReady, section.sectionId]
  )

  const handleEditorFocus = useCallback(() => {
    onSectionFocus(section.sectionId)
  }, [onSectionFocus, section.sectionId])

  return (
    <section
      id={`recyclability-document-section-${section.sectionId}`}
      className={cn(
        'flex scroll-mt-4 flex-col gap-3 border-b border-border last:border-b-0'
      )}
    >
      <div className='px-4 pt-4'>
        <h2 className='min-w-0 text-lg font-semibold'>
          {getSectionHeadingLabel(section, sectionTitle)}
        </h2>
      </div>
      <DocumentEditor
        embedded
        markdown={stripRedundantSectionHeadingFromBody(
          section.markdown,
          section.sectionId,
          sectionTitle
        )}
        editorRef={editorRef}
        onEditorReady={handleEditorReady}
        onEditorFocus={handleEditorFocus}
        contentSyncKey={contentSyncKey}
        documentBodyClassName='min-h-[160px] pt-0 pb-4'
      />
    </section>
  )
}
